import type {ApiService} from '~/services/ApiService';
import type {
  ActivityLog, ActivityLogQuery,
  AdministeredRole,
  AdministeredUser,
  AdministeredUserPaginationResponseObject,
  AdministeredWidget,
  Company,
  InviteUser,
  OrganisationSetting,
  Role,
  SetUserRoleRequest,
  UserWorkStatusRequest,
  WorkingStatus
} from '~~/api/models';

export class AdministrationService {
  private apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  async getUsers(page: number = 1, pageSize: number = 15, searchString: string | null = null, fields: string = 'id,email,displayName,companies,profilePictureUrl,isActive,workStatus'): Promise<AdministeredUserPaginationResponseObject> {
    return this.apiService.get<AdministeredUserPaginationResponseObject>('/api/administration/users', {
      params: {
        page,
        pageSize,
        fields,
        searchString,
        sort: 'displayName:asc'
      }
    });
  }

  async getAllUsers(page: number = 1, pageSize: number = 15, searchString: string | null = null, fields: string = 'id,email,displayName,companies,profilePictureUrl,isActive,workStatus'): Promise<AdministeredUserPaginationResponseObject> {
    return this.apiService.get<AdministeredUserPaginationResponseObject>('/api/administration/users/all', {
      params: {
        page,
        pageSize,
        fields,
        searchString,
        sort: 'displayName:asc'
      }
    });
  }

  async getUser(userId: string, fields: string = '*,companies,workStatus'): Promise<AdministeredUser> {
    return this.apiService.get<AdministeredUser>(`/api/administration/users/${userId}`, {
      params: {
        fields
      }
    });
  }

  async inviteUser(request: InviteUser): Promise<boolean> {
    return this.apiService.post<boolean>(`/api/administration/users/invite`, {
      body: request
    });
  }

  async resendInvitation(userId: string): Promise<boolean> {
    return this.apiService.post(`/api/administration/users/${userId}/resend-invitation`);
  }

  async revokeInvitation(userId: string): Promise<boolean> {
    return this.apiService.post(`/api/administration/users/${userId}/revoke-invitation`);
  }

  async getRoles(countUsers: boolean = false, fields: string = 'id,translationKey,descriptionTranslationKey,userCount,Icon'): Promise<Role[]> {
    return this.apiService.get<Role[]>('/api/administration/roles', {
      params: {
        countUsers,
        fields
      }
    });
  }

  async getRole(roleId: string): Promise<AdministeredRole> {
    return this.apiService.get<AdministeredRole>(`/api/administration/roles/${roleId}`);
  }

  async saveRolePermissions(permissionRequest: AdministeredRole): Promise<AdministeredRole> {
    return this.apiService.post<AdministeredRole>(`/api/administration/roles`, {
      body: permissionRequest
    });
  }

  async setUserRole(setUserRoleRequest: SetUserRoleRequest, fields: string = '*,companies(company(displayName),roleContext(role(normalizedName))),workStatus'): Promise<AdministeredUser> {
    return this.apiService.post<AdministeredUser>(`/api/user/permissions/setrole`, {
      params: {
        fields
      },
      body: setUserRoleRequest
    });
  }

  async disableUser(userId: string): Promise<boolean> {
    return this.apiService.post(`/api/user/${userId}/deactivate`);
  }

  async enableUser(userId: string): Promise<boolean> {
    return this.apiService.post(`/api/user/${userId}/activate`);
  }

  async getWidgets(): Promise<AdministeredWidget[]> {
    return this.apiService.get<AdministeredWidget[]>('/api/administration/widgets');
  }

  async getWidget(widgetId: string): Promise<AdministeredWidget> {
    try {
      return await this.apiService.get<AdministeredWidget>(`/api/administration/widgets/${widgetId}`);
    } catch (error) {
      console.error(error);
      return {} as AdministeredWidget;
    }
  }
  async saveWidget(widget: AdministeredWidget): Promise<AdministeredWidget> {
    return this.apiService.post<AdministeredWidget>(`/api/administration/widgets`, {
      body: widget
    });
  }

  async getCompanies(): Promise<Company[]> {
    return this.apiService.get<Company[]>('/api/administration/companies');
  }

  async getCompany(tenantId: string): Promise<Company> {
    return this.apiService.get<Company>(`/api/administration/companies/${tenantId}`);
  }

  async updateUserWorkStatus(workStatusRequest: UserWorkStatusRequest): Promise<WorkingStatus> {
    return this.apiService.post<WorkingStatus>(`/api/user/updateworkstatus`, {
      body: workStatusRequest
    });
  }

  async getOrganisationSettings(keys: string[]): Promise<OrganisationSetting[]> {
    return this.apiService.get<OrganisationSetting[]>(`/api/administration/organisations/settings`, {
      params: {
        keys
      }
    });
  }

  async saveOrganisationSettings(settings: OrganisationSetting[]): Promise<OrganisationSetting[]> {
    return this.apiService.post<OrganisationSetting[]>(`/api/administration/organisations/settings`, {
      body: settings
    });
  }

  async getFilteredActivities(query: ActivityLogQuery) : Promise<ActivityLog[]> {
    return this.apiService.post<ActivityLog[]>(`/api/administration/activitylog`, {
      body: query
    });
  }
}